import React, { useEffect, useState } from "react"
import DfbSpinner from "../ui-kit/dfb_spinner"
import MUserPanel from "../blocks/m_user_panel"
import MUserPanelContent from "../blocks/m_user_panel_content"
import MUserPanelAccordion, {
  StatusColor,
} from "../blocks/m_user_panel_accordion"
import {
  getApplicationsAndEvents,
  EventBookingState,
  ApplicationState,
} from "../../services/dfbNet"
import ServiceError from "../../services/ServiceError"
import { formatDate, replacePlaceholder } from "../utilities"
import content from "../../content"
import DfbParagraph from "../ui-kit/dfb_paragraph"
import { useCandidatureManagementTeaserUrl } from "../../hooks/headerLinks/candidatureManagementTeaser"

const eventStatusColors = {
  [EventBookingState.FREE]: StatusColor.GREEN,
  [EventBookingState.NEARLY_FULL]: StatusColor.ORANGE,
  [EventBookingState.FULL]: StatusColor.RED,
  [EventBookingState.UNKNOWN]: StatusColor.GREY,
}

const applicationStatusColors = {
  [ApplicationState.NO_APPLICATION_PRESENT]: StatusColor.GREY,
  [ApplicationState.IN_PROGRESS]: StatusColor.ORANGE,
  [ApplicationState.IN_REVIEW]: StatusColor.ORANGE,
  [ApplicationState.APPROVED]: StatusColor.GREEN,
  [ApplicationState.DENIED]: StatusColor.RED,
  [ApplicationState.UPDATE_REQUIRED]: StatusColor.ORANGE,
}

const MUserApplications = ({ headline }) => {
  const [noDfbNetLink, setNoDfbNetLink] = useState(false)
  const [events, setEvents] = useState(null)
  const [applications, setApplications] = useState(null)
  const [licenseDisplayName, setLicenseDisplayName] = useState("")
  const [errorMessage, setErrorMessage] = useState(null)

  const connectLink = useCandidatureManagementTeaserUrl()

  const insertLicenseName = text => {
    return replacePlaceholder(text, { licenseName: licenseDisplayName })
  }

  const getEventAccordionItem = event => {
    const begin = formatDate(event.begin)
    const end = formatDate(event.end)

    return {
      title: event.name,
      status: eventStatusColors[event.eventBookingState.key],
      buttonLabel: content.userPanels.applications.eventList.button,
      buttonLink: event.url,
      columns: [
        {
          key: content.userPanels.applications.eventList.columns.license,
          value: event.sectionName,
        },
        {
          key: content.userPanels.applications.eventList.columns.date,
          value: `${begin} - ${end}`,
        },
        {
          key: content.userPanels.applications.eventList.columns.status,
          value: event.eventBookingState.label,
        },
      ],
    }
  }

  const getApplicationAccordionItem = application => {
    return {
      title: application.educationLabel,
      status: applicationStatusColors[application.status.key],
      buttonLabel: content.userPanels.applications.applicationList.button,
      buttonLink: application.editApplicationUrl,
      columns: [
        {
          key: content.userPanels.applications.applicationList.columns.license,
          value: application.educationLabel,
        },
        {
          key: content.userPanels.applications.applicationList.columns.date,
          value: formatDate(application.date),
        },
        {
          key: content.userPanels.applications.applicationList.columns.status,
          value: application.status.label,
        },
      ],
    }
  }

  const handleApiError = err => {
    if (err.statusCode === ServiceError.STATUS_FORBIDDEN) {
      setNoDfbNetLink(true)
    } else if (err.statusCode === ServiceError.STATUS_UNAUTHORIZED) {
      setErrorMessage(content.userPanels.applications.error.unauthorized)
    } else {
      setErrorMessage(content.global.error.apiError)
    }
  }

  const initData = async () => {
    try {
      const data = await getApplicationsAndEvents()
      setLicenseDisplayName(data.licenseDisplayName || "")

      if (data.events) {
        setEvents(data.events.map(getEventAccordionItem))
      }

      if (data.applications) {
        setApplications(data.applications.map(getApplicationAccordionItem))
      }
    } catch (err) {
      handleApiError(err)
    }
  }

  useEffect(() => {
    initData()
  }, [])

  return (
    <MUserPanel headline={headline} errorMessage={errorMessage}>
      {applications && (
        <>
          <DfbParagraph
            text={
              applications.length > 0
                ? content.userPanels.applications.applicationList.copyWithData
                : content.userPanels.applications.applicationList.copyNoData
            }
            add_class="m-UserPanel-description"
          />
          {applications.length > 0 && (
            <MUserPanelAccordion items={applications} statusColumnIndex={2} />
          )}
        </>
      )}
      {events && (
        <>
          <DfbParagraph
            text={
              events.length > 0
                ? insertLicenseName(
                    content.userPanels.applications.eventList.copyWithData
                  )
                : insertLicenseName(
                    content.userPanels.applications.eventList.copyNoData
                  )
            }
            add_class="m-UserPanel-description"
          />
          {events.length > 0 && (
            <MUserPanelAccordion items={events} statusColumnIndex={2} />
          )}
        </>
      )}
      {noDfbNetLink && (
        <MUserPanelContent
          headline={content.userPanels.applications.noDfbNetLink.headline}
          richtext={content.userPanels.applications.noDfbNetLink.richtext}
          buttonLabel={content.userPanels.applications.noDfbNetLink.button}
          buttonLink={connectLink}
        />
      )}
      {!applications && !errorMessage && !noDfbNetLink && (
        <DfbSpinner type="continuous" autoLoop={true} />
      )}
    </MUserPanel>
  )
}

export default MUserApplications
